import { Button, Callout, Card, Flex, Heading, SegmentedControl, Select, Text } from '@radix-ui/themes'
import DataTable, { TableColumn } from 'react-data-table-component'
import { useEffect, useState } from 'react'
import { AlertTriangleIcon, SheetIcon, XIcon } from 'lucide-react'
import * as XLSX from 'xlsx'

import {
  dateFullToday,
  formatDateToDDMMYYYY,
  formatDateToMMYYYY,
  formatDateToYYYY_MM_DD,
  formatWithThousandSep,
} from '@/utils/utils'
import { ScreenWrapper } from '@/modules/common/components/ScreenWrapper'
import useStockFetcher from '@/hooks/useStockFetcher'
import Stock from '@/interfaces/Stock'
import { translateBody, translateFuel, translateGearbox, getDisplayValue } from '@/utils/vehicleAttributeTranslate'
import insuranceTranslate from '@/utils/insuranceTranslate'
import permitTranslate from '@/utils/permitTranslate'
import colorTranslate from '@/utils/colorTranslate'

const translateInsurance = (insurance?: string) => (insurance ? (insuranceTranslate[insurance] ?? insurance) : '')
const translatePermit = (permit?: string) => (permit ? (permitTranslate[permit] ?? permit) : '')
const translateColor = (color?: string) => (color ? (colorTranslate[color] ?? color) : '')

const priceSort = (rowA: Stock, rowB: Stock) => {
  const a = rowA.price
  const b = rowB.price

  return numericSort(a, b)
}
const expensesSort = (rowA: Stock, rowB: Stock) => {
  const a = rowA.expenses
  const b = rowB.expenses

  return numericSort(a, b)
}
const suggestedPriceSort = (rowA: Stock, rowB: Stock) => {
  const a = rowA.suggestedPrice
  const b = rowB.suggestedPrice

  return numericSort(a, b)
}
const consignmentPriceSort = (rowA: Stock, rowB: Stock) => {
  const a = rowA.consignmentPrice
  const b = rowB.consignmentPrice

  return numericSort(a, b)
}
const currentPriceSort = (rowA: Stock, rowB: Stock) => {
  const a = rowA.consignmentPrice + rowA.expenses
  const b = rowB.consignmentPrice + rowB.expenses

  return numericSort(a, b)
}
const numericSort = (a: number, b: number) => {
  if (a > b) {
    return 1
  }

  if (b > a) {
    return -1
  }

  return 0
}

const dateSort = (rowA: Stock, rowB: Stock) => {
  const a = rowA.createdAt
  const b = rowB.createdAt

  if (a > b) {
    return 1
  }

  if (b > a) {
    return -1
  }

  return 0
}

// Definicion de las columnas de la tabla
const columns: TableColumn<Stock>[] = [
  {
    name: 'Patente',
    selector: (row) => row.plate,
    sortable: true,
    grow: 0,
  },
  {
    name: 'Chassis',
    selector: (row: Stock) => row.chassisId,
    sortable: true,
    grow: 4,
  },
  {
    name: 'Marca',
    selector: (row: Stock) => row.brand,
    sortable: true,
    grow: 0,
  },
  {
    name: 'Modelo y versión',
    selector: (row: Stock) => `${row.model} ${row.version ?? ''}`,
    sortable: true,
    grow: 6,
  },
  {
    name: 'Color',
    selector: (row: Stock) => translateColor(row.color),
    sortable: true,
    grow: 2,
  },
  {
    name: 'Año',
    selector: (row: Stock) => row.year,
    minWidth: '5rem',
    sortable: true,
    grow: 0,
  },
  {
    name: 'Carrocería',
    selector: (row: Stock) => getDisplayValue(row.body_format, row.body, translateBody),
    sortable: true,
    grow: 2,
  },
  {
    name: 'Combustible',
    selector: (row: Stock) => getDisplayValue(row.fuel_format, row.fuel, translateFuel),
    sortable: true,
    grow: 2,
  },
  {
    name: 'Transmisión',
    selector: (row: Stock) => getDisplayValue(row.gearbox_format, row.gearbox, translateGearbox),
    sortable: true,
    grow: 2,
  },
  {
    name: 'Kilómetros',
    selector: (row: Stock) => formatWithThousandSep(row.odometer ?? 0),
    minWidth: '7rem',
    sortable: true,
    right: true,
    grow: 0,
  },
  {
    name: 'Estado',
    selector: (row: Stock) => (row.reservedAt ? 'Reservado' : row.publishedAt ? 'Publicado' : 'Pendiente'),
    sortable: true,
  },
  {
    name: 'Revisión Técnica',
    selector: (row: Stock) => (row.checkup ? formatDateToMMYYYY(new Date(row.checkup)) : ''),
    sortable: true,
    grow: 2,
  },
  {
    name: 'SOAP',
    selector: (row: Stock) => translateInsurance(row.insurance),
    sortable: true,
    grow: 2,
  },
  {
    name: 'Permiso de circulación',
    selector: (row: Stock) => translatePermit(row.permit),
    sortable: true,
    grow: 3,
  },
  {
    name: 'Propiedad',
    selector: (row: Stock) => (row.purchased ? 'Comprado' : 'Concesión en local'),
    sortable: true,
    grow: 5,
  },
  {
    name: 'Sucursal',
    selector: (row: Stock) => row.branch,
    sortable: true,
    grow: 0,
  },
  {
    name: 'Costo',
    selector: (row: Stock) =>
      '$' + formatWithThousandSep(row.purchased ? (row.purchasedPrice ?? 0) : (row.consignmentPrice ?? 0)),
    sortFunction: consignmentPriceSort,
    sortable: true,
    right: true,
    grow: 5,
  },
  {
    name: 'Gastos',
    selector: (row: Stock) => '$' + formatWithThousandSep(row.expenses ?? 0),
    sortFunction: expensesSort,
    sortable: true,
    right: true,
    grow: 3,
  },
  {
    name: 'Costo Total',
    selector: (row: Stock) =>
      '$' +
      formatWithThousandSep(
        (row.purchased ? (row.purchasedPrice ?? 0) : (row.consignmentPrice ?? 0)) + (row.expenses ?? 0)
      ),
    sortFunction: currentPriceSort,
    sortable: true,
    right: true,
    grow: 5,
  },
  {
    name: 'Cliente',
    selector: (row) => `${row.clientFirstname} ${row.clientLastname}`,
    sortable: true,
    grow: 4,
  },
  {
    name: 'Teléfono',
    selector: (row) => row.clientPhone,
    sortable: true,
    right: true,
    grow: 0,
  },
  {
    name: 'Consignador',
    selector: (row) => `${row.firstname} ${row.lastname}`,
    sortable: true,
    grow: 4,
  },
  {
    name: 'Precio publicación',
    selector: (row: Stock) => '$' + formatWithThousandSep(row.price ?? 0),
    sortFunction: priceSort,
    sortable: true,
    right: true,
    grow: 5,
  },
  {
    name: 'Precio sugerido',
    selector: (row: Stock) => '$' + formatWithThousandSep(row.suggestedPrice),
    sortFunction: suggestedPriceSort,
    sortable: true,
    right: true,
    grow: 5,
  },
  {
    name: 'Ingresado',
    selector: (row) => formatDateToDDMMYYYY(new Date(row.createdAt)),
    sortFunction: dateSort,
    sortable: true,
    right: true,
    grow: 3,
  },
  {
    name: 'Dias en stock',
    selector: (row: Stock) => row.daysInStock,
    sortable: true,
    right: true,
    grow: 4,
  },
]
const paginationComponentOptions = {
  rowsPerPageText: 'Filas por página',
  rangeSeparatorText: 'de',
  selectAllRowsItem: true,
  selectAllRowsItemText: 'Todos',
}

const months = [
  { value: '01', label: 'Enero' },
  { value: '02', label: 'Febrero' },
  { value: '03', label: 'Marzo' },
  { value: '04', label: 'Abril' },
  { value: '05', label: 'Mayo' },
  { value: '06', label: 'Junio' },
  { value: '07', label: 'Julio' },
  { value: '08', label: 'Agosto' },
  { value: '09', label: 'Septiembre' },
  { value: '10', label: 'Octubre' },
  { value: '11', label: 'Noviembre' },
  { value: '12', label: 'Diciembre' },
]

const currentYear = new Date().getFullYear()
const years = Array.from({ length: 11 }, (_, i) => String(currentYear - 2 + i)) // 2023 to 2033 approx

export const StockReport = () => {
  // Errores de validacion de los input
  const [stocks, setStocks] = useState<Stock[]>([])
  const [filtered, setFiltered] = useState<Stock[]>([])
  const { errorStock, fetchStocks } = useStockFetcher()
  const [selectedTab, setSelectedTab] = useState<'all' | 'consigned' | 'purchased'>('all')
  const [month, setMonth] = useState<string>('all')
  const [year, setYear] = useState<string>('all')

  const handleButtonClick = async () => {
    const data: Stock[] = await fetchStocks()

    if (!data) {
      return
    }

    setStocks(data)
  }

  useEffect(() => {
    handleButtonClick()
  }, [])

  useEffect(() => {
    let result = stocks

    // Apply Tab Filter
    if (selectedTab === 'consigned') {
      result = result.filter((s) => !s.purchased)
    } else if (selectedTab === 'purchased') {
      result = result.filter((s) => s.purchased)
    }

    // Apply Month/Year Filter
    if (month !== 'all' && year !== 'all') {
      const selectedDate = `${year}-${month}`
      result = result.filter((s) => {
        if (!s.checkup) return false
        const checkupDate = new Date(s.checkup)
        const checkupYear = checkupDate.getFullYear()
        const checkupMonth = String(checkupDate.getMonth() + 1).padStart(2, '0')
        const checkupStr = `${checkupYear}-${checkupMonth}`

        return checkupStr === selectedDate
      })
    }

    setFiltered(result)
  }, [stocks, selectedTab, month, year])

  /*
  Cambiamos los titulos
  */
  const mapDataForExport = (data: Stock[]) =>
    data.map((row) => ({
      Patente: row.plate,
      Marca: row.brand,
      'Modelo y versión': `${row.model} ${row.version ?? ''}`,
      Color: translateColor(row.color),
      Año: Number(row.year),
      Carrocería: getDisplayValue(row.body_format, row.body, translateBody),
      Combustible: getDisplayValue(row.fuel_format, row.fuel, translateFuel),
      Transmisión: getDisplayValue(row.gearbox_format, row.gearbox, translateGearbox),
      Kilómetros: Number(row.odometer),
      Estado: row.reservedAt ? 'Reservado' : row.publishedAt ? 'Publicado' : 'Pendiente',
      'Revisión Técnica': row.checkup ? formatDateToYYYY_MM_DD(new Date(row.checkup)) : '',
      SOAP: translateInsurance(row.insurance),
      'Permiso de circulación': translatePermit(row.permit),
      Chassis: row.chassisId,
      Propiedad: row.purchased ? 'Comprado' : 'Concesión en local',
      Sucursal: row.branch,
      Costo: Number(row.purchased ? (row.purchasedPrice ?? 0) : (row.consignmentPrice ?? 0)),
      Gastos: Number(row.expenses ?? 0),
      'Costo total': Number(
        (row.purchased ? (row.purchasedPrice ?? 0) : (row.consignmentPrice ?? 0)) + (row.expenses ?? 0)
      ),
      Cliente: `${row.clientFirstname} ${row.clientLastname}`,
      Teléfono: row.clientPhone,
      Consignador: `${row.firstname} ${row.lastname}`,
      'Precio publicación': Number(row.price ?? 0),
      'Precio sugerido': Number(row.suggestedPrice ?? 0),
      Ingresado: formatDateToYYYY_MM_DD(new Date(row.createdAt)),
      'Días en stock': row.daysInStock,
    }))

  const exportToExcel = () => {
    const mappedData = mapDataForExport(filtered)
    // Create a new workbook
    const workbook = XLSX.utils.book_new()

    // Convert the JSON data to a worksheet
    const worksheet = XLSX.utils.json_to_sheet(mappedData)

    // Append the worksheet to the workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Stock')

    // Generate a download
    const name = `Stock-report-${dateFullToday()}.xlsx`

    XLSX.writeFileXLSX(workbook, name, { bookType: 'xlsx' })
  }

  const clearFilters = () => {
    setMonth('all')
    setYear('all')
  }

  return (
    <ScreenWrapper mt="2">
      {errorStock && (
        <Callout.Root>
          <Callout.Icon>
            <AlertTriangleIcon />
          </Callout.Icon>
          <Callout.Text>{errorStock?.message}</Callout.Text>
        </Callout.Root>
      )}

      <Card>
        <Flex gap="4" align="center" justify="between" wrap="wrap">
          <Flex gap="4" align="center" wrap="wrap">
            <SegmentedControl.Root
              value={selectedTab}
              onValueChange={(val: 'all' | 'consigned' | 'purchased') => setSelectedTab(val)}
            >
              <SegmentedControl.Item value="all">Todos</SegmentedControl.Item>
              <SegmentedControl.Item value="consigned">Consignados</SegmentedControl.Item>
              <SegmentedControl.Item value="purchased">Comprados</SegmentedControl.Item>
            </SegmentedControl.Root>

            <Flex gap="2" align="center">
              <Text size="2" weight="bold">
                Vencimiento Rev. Técnica
              </Text>

              <Select.Root value={month} onValueChange={setMonth}>
                <Select.Trigger placeholder="Mes" />
                <Select.Content>
                  <Select.Item value="all">Mes</Select.Item>
                  {months.map((m) => (
                    <Select.Item key={m.value} value={m.value}>
                      {m.label}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>

              <Select.Root value={year} onValueChange={setYear}>
                <Select.Trigger placeholder="Año" />
                <Select.Content>
                  <Select.Item value="all">Año</Select.Item>
                  {years.map((y) => (
                    <Select.Item key={y} value={y}>
                      {y}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>

              {(month !== 'all' || year !== 'all') && (
                <Button color="gray" variant="ghost" onClick={clearFilters}>
                  <XIcon size={16} />
                </Button>
              )}
            </Flex>
          </Flex>

          <Button onClick={exportToExcel} disabled={(filtered ?? []).length === 0} color="green">
            <SheetIcon />
            <Text>Exportar</Text>
          </Button>
        </Flex>
      </Card>

      <Card>
        <Heading>Stock Total: {filtered.length}</Heading>
      </Card>

      <Card>
        <DataTable
          paginationComponentOptions={paginationComponentOptions}
          noDataComponent={<Text>[No hay datos]</Text>}
          columns={columns}
          data={filtered}
          pagination
          striped
          dense
          customStyles={{
            cells: {
              style: {
                whiteSpace: 'nowrap',
                overflow: 'visible',
                textOverflow: 'unset',
              },
            },
            headCells: {
              style: {
                whiteSpace: 'nowrap',
                fontWeight: 'bold',
              },
            },
          }}
        />
      </Card>
    </ScreenWrapper>
  )
}
